import { LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons'
import { Button, Dropdown, Layout, Space } from 'antd'
import React, { useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import LocalStorageKeys from '../../constants/local-storage-keys'
import Routes from '../../constants/routes'
import useLocalStorage from '../../hooks/useLocalStorage'
import CartButton from '../../pages/cart/CartButton'
import { actions as authActions } from '../../store/reducers/auth.reducer'
import authPersistSelectors from '../../store/selectors/auth.persist.selectors'
import NotificationsButton from '../common/notifications/NotificationButton'
import StatusAlert from '../common/status-alert/StatusAlert'
import CreateResourceDropdown from './create-resource/CreateResourceDropdown'
import GlobalSearch from './global-search/GlobalSearch'
import GlobalModals from '../common/global-modals/GlobalModals'
import './layout.scss'
import logo from '/src/assets/images/sense_logo2.png'
import logoWhite from '/src/assets/images/login-header/sense-logo-white.png'
import { ManagePromptsModal } from './manage-prompts-modal/ManagePromptsModal'
import SkipToCodeEditorLink from '../common/MonacoEditor/SkipToCodeEditorLink'

const { Header, Content, Footer, Sider } = Layout

export const HEADER_HEIGHT = 64
export const CONTENT_VERTICAL_MARGIN = HEADER_HEIGHT + 16
export const CONTENT_HORIZONTAL_MARGIN = 48
export const SIDER_WIDTH = 224
export const SIDER_COLLAPSED_WIDTH = 80
export const SIDER_ANIMATION_DURATION_IN_SEC = 0.2

const AuthorizedLayoutComponent = ({
    customSidebarMenu: siderbarMenu,
    extra,
    extraBeforeLogo,
    user,
    children,
    logout,
    unimpersonate,
    showFooter: showFooterProp = true,
}) => {
    const [siderCollapsed, setSiderCollapsed] = useLocalStorage(LocalStorageKeys.SIDER_COLLAPSED, true)
    const navigate = useNavigate()
    const location = useLocation()
    const impersonated = useSelector(authPersistSelectors.isImpersonated)
    const developerMode = useSelector(authPersistSelectors.isDeveloperMode)
    const themeMode = useSelector(state => state.theme?.mode || 'light')
    const logoSrc = themeMode === 'dark' ? logoWhite : logo

    const showFooter =
        showFooterProp &&
        [Routes.HOME, Routes.ASSIGNMENTS, Routes.PROBLEM_SETS, Routes.CATALOG, Routes.CONCEPTUAL_LABELS].includes(
            location.pathname,
        )
    const [showSetupUserPromptsModal, setShowSetupUserPromptsModal] = useState(false)

    const handleCollapseSiderClick = () => setSiderCollapsed(!siderCollapsed)

    const handleManagePromptsClick = () => {
        setShowSetupUserPromptsModal(true)
    }

    const handleLogout = () => {
        if (impersonated) {
            unimpersonate(navigate)
        } else {
            logout()
            navigate(Routes.LOGIN)
        }
    }

    const handleSettingsClick = () => navigate(Routes.SETTINGS)

    const handleSkipNavigationClick = () => {
        const mainContentElement = document.getElementById('main-content')
        mainContentElement.setAttribute('tabindex', '0')
        mainContentElement.focus({ preventScroll: true })
        mainContentElement.setAttribute('tabindex', '-1')
    }

    const userMenuItems = [
        {
            key: 'settings',
            label: 'Settings',
            onClick: handleSettingsClick,
            icon: <SettingOutlined />,
        },
        {
            key: 'logout',
            label: 'Logout',
            onClick: handleLogout,
            icon: <LogoutOutlined />,
        },
    ]

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Header className='site-layout-header' id='site-layout-header'>
                <Button className='skip-link' size='large' onClick={handleSkipNavigationClick}>
                    SKIP NAVIGATION
                </Button>
                <SkipToCodeEditorLink />
                <div className='collapse-trigger'>{extraBeforeLogo}</div>
                <div className='site-layout-header__left-block'>
                    <Link to={Routes.HOME} className='sense-logo-link' data-test="header-logo-link">
                        <img src={logoSrc} className='sense-logo' alt='sense home' data-test="header-logo" />
                    </Link>
                    <GlobalSearch />
                    <CreateResourceDropdown />
                    {extra}
                </div>
                <Space size={16}>
                    <CartButton />
                    <NotificationsButton />
                    <Dropdown
                        menu={{ items: userMenuItems }}
                        autoFocus
                        overlayStyle={{ minWidth: 180 }}
                        trigger={['click']}
                        getPopupContainer={() => document.getElementById('site-layout-header')}
                    >
                        <Button type='text' size='large'>
                            <UserOutlined /> {user.name}
                        </Button>
                    </Dropdown>
                </Space>
            </Header>
            <Layout className='site-layout'>
                {siderbarMenu ? (
                    <>
                        <Button
                            type='text'
                            aria-expanded={!siderCollapsed}
                            icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={handleCollapseSiderClick}
                            title={siderCollapsed ? 'Unfold sider menu' : 'Fold sider menu'}
                            aria-label={siderCollapsed ? 'Unfold sider menu' : 'Fold sider menu'}
                            data-test="sider-menu-toggle-button"
                            style={{
                                position: 'fixed',
                                top: HEADER_HEIGHT + 8,
                                left: siderCollapsed ? 8 : SIDER_WIDTH + 8,
                            }}
                        />
                        <Sider
                            className='site-layout-sider'
                            collapsible
                            trigger={null}
                            collapsed={siderCollapsed}
                            breakpoint='lg'
                            width={SIDER_WIDTH}
                            style={{
                                overflow: 'auto',
                                height: `calc(100vh - ${HEADER_HEIGHT}px)`,
                                position: 'fixed',
                                left: siderCollapsed ? -SIDER_COLLAPSED_WIDTH : 0,
                                top: 0,
                                zIndex: 999,
                            }}
                        >
                            {siderbarMenu}
                        </Sider>
                    </>
                ) : null}
                <Content
                    id='main-content'
                    style={{
                        margin: `${CONTENT_VERTICAL_MARGIN}px ${CONTENT_HORIZONTAL_MARGIN}px 0 ${CONTENT_HORIZONTAL_MARGIN}px`,
                        marginLeft:
                            siderCollapsed || !siderbarMenu
                                ? CONTENT_HORIZONTAL_MARGIN
                                : CONTENT_HORIZONTAL_MARGIN + SIDER_WIDTH,
                        transition: `margin-left ${SIDER_ANIMATION_DURATION_IN_SEC}s`,
                        maxWidth: '100vw',
                    }}
                >
                    {children}
                </Content>
            </Layout>
            <GlobalModals />
            {showFooter && (
                <Footer style={{ textAlign: 'center' }}>
                    <Space size='middle'>
                        <Link to={Routes.TERMS_OF_USE}>Terms</Link>
                        <Link to={Routes.PRIVACY_POLICY}>Privacy</Link>
                        <Link to={Routes.ACCESSIBILITY}>Accessibility</Link>
                        <a
                            href='https://production-kuma.azurewebsites.net/status/public-services'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            System Status
                        </a>
                    </Space>
                </Footer>
            )}
            {!!impersonated && (
                <StatusAlert
                    type='impersonation'
                    message='You are in impersonated view! Log out to leave it.'
                    onAction={handleLogout}
                    actionText='Log out'
                />
            )}
            {!!developerMode && (
                <>
                    <StatusAlert
                        type={`developer ${impersonated ? 'with-impersonation' : ''}`}
                        actionText='Manage prompts'
                        onAction={handleManagePromptsClick}
                        message="You are in developer mode! Press 'ddd111' while on the Home page to toggle it."
                    />
                    {showSetupUserPromptsModal && (
                        <ManagePromptsModal onClose={() => setShowSetupUserPromptsModal(false)} />
                    )}
                </>
            )}
        </Layout>
    )
}

const mapState = ({ auth, assignments, authorizedLayout }) => {
    return {
        user: auth.user,
        showCreateAssignmentModal: assignments.showCreateAssignmentModal,
        showCreateModelModal: assignments.showCreateModelModal,
        showCreateExcelAssignmentModal: assignments.showCreateExcelAssignmentModal,
        notifications: authorizedLayout.notifications,
    }
}

const mapDispatch = dispatch => {
    return {
        logout() {
            dispatch(authActions.logout())
        },
        unimpersonate(navigate) {
            dispatch(authActions.unimpersonate(navigate))
        },
    }
}

const AuthorizedLayout = connect(mapState, mapDispatch)(AuthorizedLayoutComponent)
export default AuthorizedLayout
